import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../utils/config';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

interface LocationData {
  districts: Array<{
    district_name: string;
    constituencies: Array<{
      constituency_name: string;
    }>;
  }>;
}

interface Organization {
  id: number;
  name: string;
  description: string;
  type: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  district: string;
  constituency: string;
  website?: string;
  created_at: string;
  updated_at: string;
}

interface CreateOrganizationData {
  name: string;
  description: string;
  type: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  district: string;
  constituency: string;
  website?: string;
}

const Organizations: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [districtFilter, setDistrictFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  });

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/organisations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }

      const data = await response.json();
      console.log('Organizations API response:', data);
      
      // Handle different possible response structures
      let orgArray = [];
      if (Array.isArray(data)) {
        orgArray = data;
      } else if (data.data && data.data.organisations && Array.isArray(data.data.organisations)) {
        orgArray = data.data.organisations;
        // Also set pagination if available
        if (data.data.pagination) {
          setPagination(data.data.pagination);
        }
      } else if (data.data && Array.isArray(data.data)) {
        orgArray = data.data;
      } else if (data.organizations && Array.isArray(data.organizations)) {
        orgArray = data.organizations;
      } else if (data.organisations && Array.isArray(data.organisations)) {
        orgArray = data.organisations;
      } else {
        console.warn('Unexpected organizations API response structure:', data);
        orgArray = [];
      }
      
      setOrganizations(orgArray);
    } catch (error: any) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationData = async () => {
    try {
      const response = await fetch('/constituencies.json');
      const data = await response.json();
      setLocationData(data.malawi_voter_registration_centers);
    } catch (error) {
      console.error('Error loading location data:', error);
    }
  };

  useEffect(() => {
    fetchOrganizations();
    fetchLocationData();
  }, []);

  const handleCreateOrganization = async (data: CreateOrganizationData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/organisations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to create organization');
      }

      toast.success('Organization created successfully');
      setShowCreateModal(false);
      fetchOrganizations();
    } catch (error: any) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization');
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'government':
        return 'bg-blue-100 text-blue-800';
      case 'ngo':
        return 'bg-green-100 text-green-800';
      case 'civil_society':
        return 'bg-green-100 text-green-800';
      case 'private':
        return 'bg-purple-100 text-purple-800';
      case 'international':
        return 'bg-orange-100 text-orange-800';
      case 'media':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredOrganizations = (Array.isArray(organizations) ? organizations : []).filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.contact_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || org.type === typeFilter;
    const matchesDistrict = districtFilter === 'all' || org.district === districtFilter;
    
    return matchesSearch && matchesType && matchesDistrict;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage organizations in the system
          </p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Organization
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <label htmlFor="search" className="block mb-1 text-sm font-medium text-gray-700">
              Search
            </label>
            <input
              type="text"
              id="search"
              placeholder="Search by name, contact person or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="type" className="block mb-1 text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              id="type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="government">Government</option>
              <option value="ngo">NGO</option>
              <option value="civil_society">Civil Society</option>
              <option value="private">Private</option>
              <option value="international">International</option>
              <option value="media">Media</option>
            </select>
          </div>
          <div>
            <label htmlFor="district" className="block mb-1 text-sm font-medium text-gray-700">
              District
            </label>
            <select
              id="district"
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Districts</option>
              {locationData?.districts.map((district) => (
                <option key={district.district_name} value={district.district_name}>
                  {district.district_name.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('all');
                setDistrictFilter('all');
              }}
              className="w-full btn btn-secondary"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Organization
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Contact Person
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Location
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500">{org.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadgeColor(org.type)}`}>
                      {org.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    {org.contact_person}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    <div>
                      <div>{org.district?.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '') || '-'}</div>
                      {org.constituency && (
                        <div className="text-xs text-gray-500">{org.constituency}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    <div>
                      <div>{org.contact_email}</div>
                      <div className="text-xs text-gray-500">{org.contact_phone}</div>
                      {org.website && (
                        <a href={org.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800">
                          {org.website}
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {formatDate(org.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrganizations.length === 0 && !loading && (
          <div className="py-12 text-center">
            <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No organizations found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria, or add a new organization.
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateOrganizationModal
          locationData={locationData}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateOrganization}
        />
      )}
    </div>
  );
};

// Create Organization Modal Component
const CreateOrganizationModal: React.FC<{
  locationData: LocationData | null;
  onClose: () => void;
  onSubmit: (data: CreateOrganizationData) => void;
}> = ({ locationData, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<CreateOrganizationData>({
    name: '',
    description: '',
    type: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    district: '',
    constituency: '',
    website: ''
  });

  const [constituencies, setConstituencies] = useState<string[]>([]);

  useEffect(() => {
    if (locationData && formData.district) {
      const district = locationData.districts.find(d => d.district_name === formData.district);
      if (district) {
        setConstituencies(district.constituencies.map(c => c.constituency_name));
      } else {
        setConstituencies([]);
      }
    }
  }, [formData.district, locationData]);

  const handleInputChange = (field: keyof CreateOrganizationData, value: string) => {
    const newData = { ...formData, [field]: value };
    
    // Reset constituency when district changes
    if (field === 'district') {
      newData.constituency = '';
    }
    
    setFormData(newData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative max-w-2xl p-5 mx-auto bg-white border rounded-md shadow-lg top-10">
        <div className="mt-3">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Create New Organization</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="name" className="block mb-1 text-sm font-medium text-gray-700">
                  Organization Name *
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="type" className="block mb-1 text-sm font-medium text-gray-700">
                  Organization Type *
                </label>
                <select
                  id="type"
                  required
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="government">Government</option>
                  <option value="ngo">NGO</option>
                  <option value="civil_society">Civil Society</option>
                  <option value="private">Private</option>
                  <option value="international">International</option>
                  <option value="media">Media</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block mb-1 text-sm font-medium text-gray-700">
                Description *
              </label>
              <textarea
                id="description"
                required
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of the organization..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="contact_person" className="block mb-1 text-sm font-medium text-gray-700">
                  Contact Person *
                </label>
                <input
                  type="text"
                  id="contact_person"
                  required
                  value={formData.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="contact_email" className="block mb-1 text-sm font-medium text-gray-700">
                  Contact Email *
                </label>
                <input
                  type="email"
                  id="contact_email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="contact_phone" className="block mb-1 text-sm font-medium text-gray-700">
                  Contact Phone *
                </label>
                <input
                  type="tel"
                  id="contact_phone"
                  required
                  value={formData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+265999123456"
                />
              </div>
              <div>
                <label htmlFor="website" className="block mb-1 text-sm font-medium text-gray-700">
                  Website
                </label>
                <input
                  type="url"
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="district" className="block mb-1 text-sm font-medium text-gray-700">
                  District *
                </label>
                <select
                  id="district"
                  required
                  value={formData.district}
                  onChange={(e) => handleInputChange('district', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select District</option>
                  {locationData?.districts.map((district) => (
                    <option key={district.district_name} value={district.district_name}>
                      {district.district_name.replace(' DISTRICT COUNCIL', '').replace(' CITY COUNCIL', '')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="constituency" className="block mb-1 text-sm font-medium text-gray-700">
                  Constituency *
                </label>
                <select
                  id="constituency"
                  required
                  value={formData.constituency}
                  onChange={(e) => handleInputChange('constituency', e.target.value)}
                  disabled={!formData.district}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !formData.district ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">Select Constituency</option>
                  {constituencies.map((constituency) => (
                    <option key={constituency} value={constituency}>
                      {constituency}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                Create Organization
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Organizations;